import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { useMrcHistoryReport } from "../../hooks/useMrcHistoryReport";
import Report20DataLoadingOverlay from "./Report20DataLoadingOverlay";
import { SpinnerCustom } from "../ui/Spinner";

import CustomSelect from "../ui/CustomSelect";
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const CHANGE_FILTERS = [
  { id: "all", label: "All" },
  { id: "changes", label: "With changes" },
  { id: "increases", label: "Increases" },
  { id: "decreases", label: "Decreases" },
];

const PROVIDER_BADGE_STYLES = [
  { bg: "bg-orange-100", text: "text-orange-800", border: "border-orange-200" },
  { bg: "bg-violet-100", text: "text-violet-800", border: "border-violet-200" },
  { bg: "bg-teal-100", text: "text-teal-800", border: "border-teal-200" },
  { bg: "bg-amber-100", text: "text-amber-900", border: "border-amber-200" },
  { bg: "bg-sky-100", text: "text-sky-800", border: "border-sky-200" },
  { bg: "bg-rose-100", text: "text-rose-800", border: "border-rose-200" },
];

function formatCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatCompactCurrency(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  const abs = Math.abs(num);
  if (abs >= 1_000_000) {
    return `$${(num / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 10_000) {
    return `$${Math.round(num / 1_000).toLocaleString("en-US")}K`;
  }
  return formatCurrency(num);
}

function providerBadgeStyle(provider) {
  const name = String(provider || "");
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash + name.charCodeAt(i) * (i + 1)) % PROVIDER_BADGE_STYLES.length;
  }
  return PROVIDER_BADGE_STYLES[hash];
}

function getMonthCellChange(mrcHistory, monthKeys, monthKey) {
  const idx = monthKeys.indexOf(monthKey);
  if (idx <= 0) return null;
  const current = mrcHistory[monthKey];
  if (current == null) return null;

  let prev = null;
  for (let i = idx - 1; i >= 0; i -= 1) {
    const priorKey = monthKeys[i];
    const priorVal = mrcHistory[priorKey];
    if (priorVal != null) {
      prev = Number(priorVal);
      break;
    }
  }
  if (prev == null) return null;

  const curr = Number(current);
  if (!Number.isFinite(curr) || curr === prev) return null;

  const delta = Math.round((curr - prev) * 100) / 100;
  return {
    direction: delta > 0 ? "up" : "down",
    delta: Math.abs(delta),
  };
}

function formatSignedMo(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  if (num > 0) return `+${formatCurrency(num)}/mo`;
  if (num < 0) return `${formatCurrency(num)}/mo`;
  return `${formatCurrency(0)}/mo`;
}

function SummaryCard({ label, value, sublabel, valueClassName = "text-[var(--ink,#1c1c1a)]" }) {
  return (
    <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
        {label}
      </p>
      <p className={`mt-1 text-[22px] font-semibold tabular-nums leading-tight ${valueClassName}`}>
        {value}
      </p>
      {sublabel ? (
        <p className="mt-1 text-[12px] leading-snug text-[var(--muted,#6b6a66)]">{sublabel}</p>
      ) : null}
    </div>
  );
}

function MrcHistoryHeader({ year, onYearChange, yearOptions, latestMonthLabel }) {
  return (
    <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 mb-5">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
          Reports
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <h1 className="text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
            MRC History
          </h1>
          <span className="inline-flex rounded-full bg-[var(--band,#eef3fa)] px-2 py-0.5 text-[11px] font-semibold text-[#3b6ea8]">
            Data circuits only
          </span>
        </div>
        <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)]">
          Monthly recurring charge per data circuit, month by month, with changes highlighted
        </p>
      </div>
      {onYearChange ? (
        <div className="inline-flex items-stretch overflow-hidden rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white shadow-sm">
          <div className="px-3 py-2 border-r border-[var(--line,#e4e2dc)]">
            <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
              Fiscal year
            </label>
            <CustomSelect
              value={year ?? ""}
              onChange={(e) => onYearChange(parseInt(e.target.value, 10))}
              className="mt-0.5 h-7 bg-transparent pr-6 text-[13px] font-medium text-[var(--ink,#1c1c1a)] focus:outline-none"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </CustomSelect>
          </div>
          <div className="px-3 py-2">
            <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
              Latest billed month
            </label>
            <div className="mt-0.5 h-7 inline-flex items-center text-[13px] font-medium text-[var(--ink,#1c1c1a)]">
              {latestMonthLabel || "—"}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const Report20MrcHistoryView = ({ tenantId, year, onYearChange }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [changeFilter, setChangeFilter] = useState("all");
  const [providerFilter, setProviderFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const mrcYearOptions = useMemo(() => [new Date().getUTCFullYear()], []);

  const { data: report, loading, error, refresh } = useMrcHistoryReport(tenantId, year, {
    enabled: Boolean(tenantId && year),
    page: currentPage,
    limit: pageSize,
    search: searchTerm,
    changeFilter,
    provider: providerFilter,
    category: categoryFilter,
    sortKey,
    sortDir,
  });

  const months = useMemo(() => report?.months || [], [report]);
  const monthKeys = useMemo(() => months.map((m) => m.key), [months]);
  const paginatedRows = useMemo(() => report?.rows || [], [report]);
  const summary = report?.summary || {};
  const latestMonthLabel = summary.currentMonthLabel
    ? `${summary.currentMonthLabel} ${year}`
    : null;
  const providers = useMemo(() => report?.providers || [], [report]);
  const categories = useMemo(() => report?.categories || [], [report]);
  const filterCounts = report?.filterCounts || { all: 0, changes: 0, increases: 0, decreases: 0 };
  const pagination = report?.pagination || {};
  const totalRecords = pagination.totalRecords ?? 0;
  const filteredRecords = pagination.totalFiltered ?? 0;
  const totalPages = pagination.totalPages ?? 1;
  const startRecord =
    filteredRecords === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endRecord =
    filteredRecords === 0 ? 0 : Math.min(pagination.page * pagination.limit, filteredRecords);

  const visiblePageNumbers = useMemo(() => {
    const page = pagination.page || currentPage;
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (page <= 3) {
      return [1, 2, 3, 4, 5];
    }
    if (page >= totalPages - 2) {
      return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [page - 2, page - 1, page, page + 1, page + 2];
  }, [pagination.page, currentPage, totalPages]);

  const handleSort = useCallback(
    (key) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    [sortKey]
  );

  const handleProviderBadgeClick = useCallback((provider) => {
    setProviderFilter((prev) => (prev === provider ? "all" : provider));
  }, []);

  useEffect(() => {
    const currentYear = new Date().getUTCFullYear();
    if (onYearChange && year !== currentYear) {
      onYearChange(currentYear);
    }
  }, [onYearChange, year]);

  useEffect(() => {
    setChangeFilter("all");
    setProviderFilter("all");
    setCategoryFilter("all");
    setSearchTerm("");
    setSortKey(null);
    setSortDir("asc");
    setCurrentPage(1);
    setPageSize(20);
  }, [year]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize, providerFilter, categoryFilter, changeFilter, sortKey, sortDir]);

  const netChangePositive = Number(summary.netChangeYtd) > 0;
  const netChangeNegative = Number(summary.netChangeYtd) < 0;

  const sortIndicator = (key) => {
    if (sortKey !== key) return null;
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  const dimStaleWhileLoading = "pointer-events-none opacity-45 select-none transition-opacity";
  const activePage = pagination.page || currentPage;

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center min-h-[min(60vh,420px)]">
        <SpinnerCustom />
      </div>
    );
  }

  if (error && !report) {
    return (
      <>
        <MrcHistoryHeader
          year={year}
          onYearChange={onYearChange}
          yearOptions={mrcYearOptions}
          latestMonthLabel={latestMonthLabel}
        />
        <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white p-8 max-w-md mx-auto text-center">
          <p className="text-sm font-medium text-[var(--crit,#c0262b)] mb-3">
            {error.message || "Failed to load MRC history report"}
          </p>
          <button
            type="button"
            onClick={() => refresh && refresh()}
            className="inline-flex items-center justify-center rounded-md bg-[var(--accent,#a51c1c)] px-4 py-2 text-[13px] font-medium text-white hover:opacity-90"
          >
            Retry
          </button>
        </div>
      </>
    );
  }

  if (!report || totalRecords === 0) {
    return (
      <>
        <MrcHistoryHeader
          year={year}
          onYearChange={onYearChange}
          yearOptions={mrcYearOptions}
          latestMonthLabel={latestMonthLabel}
        />
        <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-12 text-center text-[13px] text-[var(--muted,#6b6a66)]">
          No monthly snapshot data found for {year || "the selected year"}.
        </div>
      </>
    );
  }

  return (
    <>
      <MrcHistoryHeader
        year={year}
        onYearChange={onYearChange}
        yearOptions={mrcYearOptions}
        latestMonthLabel={latestMonthLabel}
      />

      <div className="relative min-h-[min(60vh,420px)]">
        <Report20DataLoadingOverlay show={loading && Boolean(report)} message="Loading MRC history…" />
        <div className={loading && report ? dimStaleWhileLoading : ""}>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        <SummaryCard
          label="Active data circuits"
          value={summary.activeCircuits?.toLocaleString("en-US") ?? "0"}
          sublabel={`Across ${summary.locationCount?.toLocaleString("en-US") ?? 0} locations${
            summary.currentMonthLabel ? `, billed in ${summary.currentMonthLabel}` : ""
          }`}
        />
        <SummaryCard
          label="Net change YTD"
          value={formatSignedMo(summary.netChangeYtd)}
          sublabel={`${summary.circuitsWithChanges ?? 0} circuits changed since January`}
          valueClassName={
            netChangePositive
              ? "text-[var(--accent,#a51c1c)]"
              : netChangeNegative
              ? "text-[var(--good,#1f6b34)]"
              : "text-[var(--ink,#1c1c1a)]"
          }
        />
        <SummaryCard
          label="Increases"
          value={(summary.increaseCount ?? filterCounts.increases ?? 0).toLocaleString("en-US")}
          sublabel="Circuits with MRC increases"
          valueClassName="text-[var(--accent,#a51c1c)]"
        />
        <SummaryCard
          label="Decreases"
          value={(summary.decreaseCount ?? filterCounts.decreases ?? 0).toLocaleString("en-US")}
          sublabel="Circuits with MRC decreases"
          valueClassName="text-[var(--good,#1f6b34)]"
        />
      </div>

      <div className="flex flex-wrap items-end gap-x-5 gap-y-1 mb-3 border-b border-[var(--line,#e4e2dc)]">
        {CHANGE_FILTERS.map((f) => {
          const count =
            f.id === "all"
              ? filterCounts.all
              : f.id === "changes"
              ? filterCounts.changes
              : f.id === "increases"
              ? filterCounts.increases
              : filterCounts.decreases;
          const active = changeFilter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setChangeFilter(f.id)}
              className={`inline-flex items-center gap-1.5 pb-2 text-[13px] border-b-2 -mb-px ${
                active
                  ? "border-[var(--accent,#a51c1c)] font-semibold text-[var(--accent,#a51c1c)]"
                  : "border-transparent text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)]"
              }`}
            >
              {f.label}
              <span
                className={`tabular-nums text-[12px] ${
                  active ? "text-[var(--accent,#a51c1c)]" : "text-[var(--faint,#9a9a95)]"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mb-4 rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white px-4 py-3 flex flex-col lg:flex-row lg:items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
            Search
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--faint,#9a9a95)] pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Address, circuit ID or service type"
              className="h-[38px] w-full pl-9 pr-3 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-[13px] text-[var(--ink,#1c1c1a)] focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)]"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
            Provider
          </label>
          <CustomSelect
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="h-[38px] min-w-[10rem] px-2.5 text-[13px] border border-[var(--line-strong,#d5d3cc)] rounded-md bg-white text-[var(--ink,#1c1c1a)] focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
          >
            <option value="all">All providers</option>
            {providers.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </CustomSelect>
        </div>

        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-1">
            Category
          </label>
          <CustomSelect
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="h-[38px] min-w-[10rem] px-2.5 text-[13px] border border-[var(--line-strong,#d5d3cc)] rounded-md bg-white text-[var(--ink,#1c1c1a)] focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
          >
            <option value="all">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </CustomSelect>
        </div>

        <p className="text-[12px] text-[var(--muted,#6b6a66)] lg:ml-auto whitespace-nowrap pb-2">
          Showing {filteredRecords.toLocaleString("en-US")} of {totalRecords.toLocaleString("en-US")}{" "}
          data circuits
        </p>
      </div>

      <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border-collapse">
          <thead className="bg-[var(--sunken2,#fbfaf8)] sticky top-0 z-30">
            <tr>
              <th
                className="sticky left-0 z-40 bg-[var(--sunken2,#fbfaf8)] px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)] border-b border-r border-[var(--line,#e4e2dc)] min-w-[200px] cursor-pointer hover:text-[var(--ink,#1c1c1a)]"
                onClick={() => handleSort("address")}
              >
                Site{sortIndicator("address")}
              </th>
              <th
                className="sticky left-[200px] z-40 bg-[var(--sunken2,#fbfaf8)] px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)] border-b border-r border-[var(--line,#e4e2dc)] min-w-[200px] cursor-pointer hover:text-[var(--ink,#1c1c1a)]"
                onClick={() => handleSort("circuitId")}
              >
                Circuit{sortIndicator("circuitId")}
              </th>
              {months.map((month) => (
                <th
                  key={month.key}
                  className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)] border-b border-[var(--line,#e4e2dc)] whitespace-nowrap min-w-[88px] cursor-pointer hover:text-[var(--ink,#1c1c1a)]"
                  onClick={() => handleSort(month.key)}
                >
                  {month.label}
                  {sortIndicator(month.key)}
                </th>
              ))}
              <th
                className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)] border-b border-[var(--line,#e4e2dc)] whitespace-nowrap cursor-pointer hover:text-[var(--ink,#1c1c1a)]"
                onClick={() => handleSort("changeCount")}
              >
                # Changes{sortIndicator("changeCount")}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map((row, rowIdx) => {
              const badge = providerBadgeStyle(row.provider);
              return (
                <tr
                  key={row.circuitId}
                  className={rowIdx % 2 === 0 ? "bg-white" : "bg-slate-50/40"}
                >
                  <td
                    className={`sticky left-0 z-10 px-4 py-2.5 border-b border-r border-slate-100 text-slate-700 min-w-[200px] max-w-[240px] ${
                      rowIdx % 2 === 0 ? "bg-white" : "bg-slate-50"
                    }`}
                  >
                    <span className="line-clamp-2 text-xs leading-snug">{row.address || "No Address"}</span>
                  </td>
                  <td
                    className={`sticky left-[200px] z-10 px-4 py-2.5 border-b border-r border-slate-100 min-w-[200px] ${
                      rowIdx % 2 === 0 ? "bg-white" : "bg-slate-50"
                    }`}
                  >
                    <div className="font-semibold text-slate-900 text-xs whitespace-nowrap">
                      {row.circuitId}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      {row.provider && row.provider !== "—" ? (
                        <button
                          type="button"
                          onClick={() => handleProviderBadgeClick(row.provider)}
                          className={`inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide border ${badge.bg} ${badge.text} ${badge.border} hover:opacity-80`}
                        >
                          {row.provider}
                        </button>
                      ) : null}
                      {row.serviceType && row.serviceType !== "—" ? (
                        <span className="text-[10px] text-slate-500 uppercase">{row.serviceType}</span>
                      ) : null}
                    </div>
                  </td>
                  {months.map((month) => {
                    const value = row.mrcHistory?.[month.key];
                    const change = getMonthCellChange(row.mrcHistory, monthKeys, month.key);
                    const isUp = change?.direction === "up";
                    const isDown = change?.direction === "down";

                    return (
                      <td
                        key={`${row.circuitId}-${month.key}`}
                        className={`px-3 py-2 border-b border-slate-100 text-right align-top whitespace-nowrap ${
                          isUp
                            ? "bg-red-50/80 border-l-[3px] border-l-red-500"
                            : isDown
                            ? "bg-emerald-50/80 border-l-[3px] border-l-emerald-500"
                            : ""
                        }`}
                      >
                        {value == null ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <div>
                            <div className="font-medium text-slate-800 tabular-nums text-xs">
                              {formatCurrency(value)}
                            </div>
                            {change ? (
                              <div
                                className={`text-[10px] font-medium mt-0.5 tabular-nums ${
                                  isUp ? "text-red-600" : "text-emerald-600"
                                }`}
                              >
                                {isUp ? "↑" : "↓"} {formatCurrency(change.delta)}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5 border-b border-slate-100 text-center">
                    {row.changeCount > 0 ? (
                      <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded-full bg-sky-100 text-sky-800 text-xs font-bold">
                        {row.changeCount}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!paginatedRows.length && (
              <tr>
                <td
                  colSpan={months.length + 3}
                  className="px-4 py-10 text-center text-sm text-slate-500"
                >
                  No circuits match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="py-1.5 px-3 border-t border-gray-200 bg-gray-50">
        <div className="flex flex-col lg:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-1.5">
            <label className="text-xs text-gray-700">Show:</label>
            <CustomSelect
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              className="border border-gray-300 rounded-md py-0.5 px-1 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </CustomSelect>
            <span className="text-xs text-gray-700">per page</span>
          </div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCurrentPage(1)}
              disabled={activePage === 1 || filteredRecords === 0}
              className="px-2 py-0.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="First page"
            >
              ««
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={activePage === 1 || filteredRecords === 0}
              className="px-2 py-0.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <div className="flex items-center gap-0.5">
              {visiblePageNumbers.map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-2 py-0.5 text-xs font-medium rounded-md ${
                    activePage === pageNum
                      ? "bg-[var(--accent,#a51c1c)] text-white"
                      : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={activePage >= totalPages || filteredRecords === 0}
              className="px-2 py-0.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage(totalPages)}
              disabled={activePage >= totalPages || filteredRecords === 0}
              className="px-2 py-0.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Last page"
            >
              »»
            </button>
          </div>

          <div className="text-xs text-gray-700">
            Showing {startRecord.toLocaleString("en-US")} - {endRecord.toLocaleString("en-US")} of{" "}
            {filteredRecords.toLocaleString("en-US")} circuits ({totalRecords.toLocaleString("en-US")}{" "}
            total records)
          </div>
        </div>
      </div>

      <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-slate-500">
        <div className="flex flex-wrap items-center gap-4">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-red-50 border-l-[3px] border-l-red-500" />
            MRC increased from prior month
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm bg-emerald-50 border-l-[3px] border-l-emerald-500" />
            MRC decreased from prior month
          </span>
        </div>
        <p>Click column headers to sort. Click provider badges to filter.</p>
      </div>
    </div>
        </div>
      </div>
    </>
  );
};

export default Report20MrcHistoryView;
